/**
 * useUniqueValidation.js
 * Custom Hook para validar campos únicos
 * Zero Hardcode - Modular y reutilizable
 */

import { useState, useCallback, useRef } from 'react';
import { 
  shouldValidateUnique, 
  buildUniqueValidationUrl, 
  getUniqueErrorMessage,
  hasUniqueValidation 
} from '../utils/uniqueValidator';
import { secureFetch } from '../utils/authInterceptor';

/**
 * Hook para manejar validaciones unique
 * 
 * @param {string} componentForm - Nombre del componente/tabla
 * @param {string} mode - Modo del formulario (create/update/view/delete)
 * @param {any} idValue - Valor actual del ID
 * @returns {object} - Estados y funciones de validación
 */
export function useUniqueValidation(componentForm, mode, idValue) {
  
  // ==========================================
  // ESTADOS
  // ==========================================
  
  // Campos que están validando actualmente
  const [validating, setValidating] = useState({});
  
  // Errores de validación unique
  const [errors, setErrors] = useState({});
  
  // Refs para debounce
  const timeoutsRef = useRef({});

  // ==========================================
  // LIMPIAR TIMEOUT
  // ==========================================
  
  const clearFieldTimeout = useCallback((fieldName) => {
    if (timeoutsRef.current[fieldName]) {
      clearTimeout(timeoutsRef.current[fieldName]);
      delete timeoutsRef.current[fieldName];
    }
  }, []);

  // ==========================================
  // LIMPIAR ERROR
  // ==========================================
  
  const clearError = useCallback((fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  // ==========================================
  // VALIDAR UNIQUE (CON DEBOUNCE)
  // ==========================================
  
  const validate = useCallback(async (field, value) => {
    
    // 🔥 VALIDACIÓN 1: ¿Debe validar? (solo CREATE con ID null/0)
    if (!shouldValidateUnique(mode, idValue)) {
      return;
    }

    // 🔥 VALIDACIÓN 2: ¿Campo tiene unique?
    if (!hasUniqueValidation(field)) {
      return;
    }

    // 🔥 VALIDACIÓN 3: ¿Valor vacío?
    if (!value || value === '') {
      clearError(field.name);
      return;
    }

    // Limpiar timeout anterior
    clearFieldTimeout(field.name);

    // Crear nuevo timeout (debounce 500ms)
    timeoutsRef.current[field.name] = setTimeout(async () => {
      
      // Marcar como validando
      setValidating(prev => ({ ...prev, [field.name]: true }));

      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const url = buildUniqueValidationUrl(field, value, componentForm);

        const response = await secureFetch(url, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        });

        const json = await response.json();

        // Si existe → Error
        if (json.success && json.data.exists) {
          const message = getUniqueErrorMessage(field);
          setErrors(prev => ({ 
            ...prev, 
            [field.name]: { 
              message, 
              shouldClear: true  // 🔥 Bandera para limpiar
            } 
          }));
        } 
        // Si NO existe → Limpiar error
        else {
          clearError(field.name);
        }

      } catch (error) {
        console.error(`Error validando unique en ${field.name}:`, error);
        // En caso de error de red, limpiar error (no bloquear)
        clearError(field.name);
      } finally {
        // Desmarcar validando
        setValidating(prev => {
          const newValidating = { ...prev };
          delete newValidating[field.name];
          return newValidating;
        });
      }

    }, 500); // Debounce 500ms

  }, [componentForm, mode, idValue, clearError, clearFieldTimeout]);

  // ==========================================
  // LIMPIAR TODO (cleanup)
  // ==========================================
  
  const reset = useCallback(() => {
    // Limpiar todos los timeouts
    Object.keys(timeoutsRef.current).forEach(clearFieldTimeout);
    
    // Limpiar estados
    setValidating({});
    setErrors({});
  }, [clearFieldTimeout]);

  // ==========================================
  // VERIFICAR SI HAY ERRORES
  // ==========================================
  
  const hasErrors = Object.keys(errors).length > 0;
  const isValidating = Object.values(validating).some(Boolean);

  // ==========================================
  // RETURN
  // ==========================================
  
  return {
    // Estados
    validating,        // { fieldName: true/false }
    errors,            // { fieldName: "mensaje" }
    hasErrors,         // boolean
    isValidating,      // boolean
    
    // Funciones
    validate,          // (field, value) => Promise<void>
    clearError,        // (fieldName) => void
    reset              // () => void
  };
}